"use client";
import { groupBy, isEmpty } from "lodash";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useMemo } from "react";
import { BsCheckCircleFill, BsCircle } from "react-icons/bs";
import { generateIcon, useLocalStorage } from "utils";
import { BooleanStatus } from "../boolean-status";
import { MediaEnum } from "./enums";
import type { FilterMediaByType, PhotoItemType, VideoItemType } from "./types";
import { useSearchParams } from "next/navigation";

// Helper function to extract grouping key from backgroundFileName
const getBackgroundGroupKey = (fileName: string | null): string | null => {
  if (!fileName) return null;
  if (fileName.includes("_")) {
    return fileName.split("_")[0];  // Return prefix before first underscore
  }
  return fileName;
};

interface IPictureFilterProps {
  items: (VideoItemType | PhotoItemType)[];
  filterBy: FilterMediaByType;
  setFilterBy: React.Dispatch<React.SetStateAction<FilterMediaByType>>;
  showPrivacyFilter: boolean;
  setShowPrivacyFilter: React.Dispatch<React.SetStateAction<boolean>>;
  privacyFilterItems: boolean[];
  setPrivacyFilterItems: React.Dispatch<React.SetStateAction<boolean[]>>;
  activeBackgroundFilterItems: string[];
  setActiveBackgroundFilterItems: React.Dispatch<
    React.SetStateAction<string[]>
  >;
  showFilter: boolean;
}

interface ISelectableItemWrapperProps {
  children: React.ReactNode;
  active: string[];
  activeBackgroundFilterItems: string[];
  activateFilter: () => void;
}

function SelectableItemWrapper({
  children,
  active,
  activeBackgroundFilterItems,
  activateFilter,
}: ISelectableItemWrapperProps) {
  const getSelectBtnClassName = () => {
    return !isEmpty(active)
      ? `text-purple-950 left-1 top-1 bg-white rounded-full block`
      : `text-white ${
          activeBackgroundFilterItems.length > 0
            ? "opacity-60"
            : "opacity-40 lg:opacity-0 group-hover/item:opacity-60 group-hover/item:hover:opacity-100"
        }`;
  };

  const getSelectButtonIcon = () => {
    if (isEmpty(active)) {
      return generateIcon(
        BsCircle,
        "text-[16px] scale-[1.35] box-shadow-[0 0 0px 1px #fff inset]"
      );
    }

    return generateIcon(BsCheckCircleFill, "text-[16px] scale-[1.35]");
  };

  return (
    <div className="relative group/item bg-gray-200">
      <span
        className="absolute top-[2px] left-[2px] z-30 cursor-pointer p-3 rounded-full"
        onClick={activateFilter}
      >
        <span className={getSelectBtnClassName()} title="select">
          {getSelectButtonIcon()}
        </span>
      </span>

      <div
        className="transition-all overflow-hidden w-[150px] h-[100%]"
        style={{ willChange: "padding" }}
      >
        {children}
      </div>
    </div>
  );
}

export default function PictureFilter({
  items,
  filterBy,
  setFilterBy,
  showPrivacyFilter,
  setShowPrivacyFilter,
  privacyFilterItems,
  setPrivacyFilterItems,
  activeBackgroundFilterItems,
  setActiveBackgroundFilterItems,
}: IPictureFilterProps) {
  const t = useTranslations();
  const [showBackgroundFilter, setShowBackgroundFilter] =
    useLocalStorage<boolean>("pictureBackgroundFilterEnabled", false);
  const [showPrivacyOpenedFilter, setShowPrivacyOpenedFilter] =
    useLocalStorage<boolean>("picturePrivacyOpenedFilterEnabled", false);
  const [showPrivacyAcceptedFilter, setShowPrivacyAcceptedFilter] =
    useLocalStorage<boolean>("picturePrivacyAcceptedFilterEnabled", false);
  const [showPrivacyRejectedFilter, setShowPrivacyRejectedFilter] =
    useLocalStorage<boolean>("picturePrivacyRejectedFilterEnabled", false);
  const [showPrivacyDownloadedFilter, setShowPrivacyDownloadedFilter] =
    useLocalStorage<boolean>("picturePrivacyDownloadedFilterEnabled", false);
  const [allFilter, setAllFilter] = useLocalStorage<{
    selectAll: boolean;
    original: boolean;
  }>("pictureBackgroundFilterAll", {
    selectAll: true,
    original: false,
  });
  const searchParams = useSearchParams();
  const galleryFilterStatus = searchParams.get("f");

  // Group by the background group key (prefix before first underscore)
  const backgroundTypeList = groupBy(items, (item) => {
    // Only process photo items that have backgroundFileName
    if (item.mediaType === MediaEnum.Photo) {
      return getBackgroundGroupKey((item as PhotoItemType).backgroundFileName);
    }
    return null;
  });
  const backgroundTypes: any[] = [];
  const newActiveBackgroundFilterItems: string[] = [
    ...activeBackgroundFilterItems,
  ];
  Object.values(backgroundTypeList).forEach((backgroundType) => {
    backgroundTypes.push(backgroundType[0]);
  });

  const allBacgroundFilter = (withNull?: boolean) => {
    backgroundTypes.forEach((background) => {
      if (background.backgroundFileName) {
        const groupKey = getBackgroundGroupKey(background.backgroundFileName);
        if (groupKey) {
          newActiveBackgroundFilterItems.push(groupKey);
        }
      }
    });
    let newArray = withNull ? [null] : [];
    newArray = [
      ...newArray,
      ...newActiveBackgroundFilterItems.filter((nabfi) => nabfi),
    ];
    setActiveBackgroundFilterItems(newArray);
  };

  useEffect(
    () => {
      if (showBackgroundFilter) {
        if (allFilter.selectAll && !allFilter.original) {
          allBacgroundFilter();
          setAllFilter({ selectAll: true, original: allFilter.original });
        } else if (!allFilter.selectAll && allFilter.original) {
          setActiveBackgroundFilterItems([null]);
          setAllFilter({ selectAll: false, original: allFilter.original });
        } else if (allFilter.selectAll && allFilter.original) {
          // newActiveBackgroundFilterItems.push(null);
          allBacgroundFilter(true);
        }
      }
    },
    // eslint-disable-next-line
    [showBackgroundFilter]
  );

  useEffect(() => {
    setPrivacyFilterItems([
      showPrivacyOpenedFilter,
      showPrivacyAcceptedFilter,
      showPrivacyRejectedFilter,
      showPrivacyDownloadedFilter,
    ]);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!filterBy.photo) {
      setShowPrivacyFilter(false);
      setShowBackgroundFilter(false);
      setAllFilter({ selectAll: false, original: false });
    }
    // eslint-disable-next-line
  }, [filterBy]);

  const activateAllFilter = () => {
    if (allFilter.selectAll) {
      setActiveBackgroundFilterItems([null]);
      setAllFilter({ selectAll: false, original: true });
    } else if (!allFilter.selectAll) {
      allBacgroundFilter();
      setAllFilter({ selectAll: true, original: false });
    }
  };

  const updatePrivacyFilter = (index: number, value: boolean) => {
    const newPrivacyFilterItems = [...privacyFilterItems];
    newPrivacyFilterItems[index] = value;
    setPrivacyFilterItems(newPrivacyFilterItems);
  };

  const hidePrivacyWhen = useMemo(() => {
    return galleryFilterStatus !== null;
  }, [galleryFilterStatus]);

  return filterBy.photo ? (
    <div className="flex flex-col w-[100%]">
      {!showPrivacyFilter && !showBackgroundFilter ? (
        <div className="flex flex-wrap sm:flex-nowrap items-center text-sm rounded-md cursor-pointer gap-4">
          <div
            className={`flex items-center ${hidePrivacyWhen ? "hidden" : ""}`}
          >
            <div className="mr-4">
              {t("Gallery.picturePrivacyFilter.title")}:
            </div>
            <div
              className="mr-4 label flex items-center cursor-pointer"
              onClick={() => {
                setShowPrivacyFilter(!showPrivacyFilter);
                if (!showPrivacyFilter) {
                  setFilterBy({ photo: true, video: false });
                }
              }}
            >
              <BooleanStatus value={showPrivacyFilter} />
            </div>
          </div>
          <div className="flex items-center">
            <div className="mr-4">{t("Gallery.picturebackgroundFilter")}:</div>
            <div
              className="label flex items-center cursor-pointer"
              onClick={() => {
                setActiveBackgroundFilterItems([]);
                setShowBackgroundFilter(!showBackgroundFilter);
                if (!showBackgroundFilter) {
                  setFilterBy({ photo: true, video: false });
                }
              }}
            >
              <BooleanStatus value={showBackgroundFilter} />
            </div>
          </div>
        </div>
      ) : null}
      {showPrivacyFilter || showBackgroundFilter ? (
        <div
          className={`flex items-center text-sm rounded-md cursor-pointer ${hidePrivacyWhen ? "hidden" : ""}`}
        >
          <div className="mr-4">{t("Gallery.picturePrivacyFilter.title")}:</div>
          <div
            className="mr-4 label flex items-center cursor-pointer"
            onClick={() => {
              setShowPrivacyFilter(!showPrivacyFilter);
              if (!showPrivacyFilter) {
                setFilterBy({ photo: true, video: false });
              }
            }}
          >
            <BooleanStatus value={showPrivacyFilter} />
          </div>
        </div>
      ) : null}
      {showPrivacyFilter ? (
        <div className="flex flex-wrap sm:flex-nowrap items-center text-sm rounded-md cursor-pointer gap-4">
          <div className="flex items-center mr-4">
            <div className="mr-2">
              {t("Gallery.picturePrivacyFilter.opened")}:
            </div>
            <div
              className="label flex items-center cursor-pointer"
              onClick={() => {
                const newValue = !showPrivacyOpenedFilter;
                updatePrivacyFilter(0, newValue);
                setShowPrivacyOpenedFilter(newValue);
              }}
            >
              <BooleanStatus value={showPrivacyOpenedFilter} />
            </div>
          </div>
          <div className="flex items-center mr-4">
            <div className="mr-2">
              {t("Gallery.picturePrivacyFilter.accepted")}:
            </div>
            <div
              className="label flex items-center cursor-pointer"
              onClick={() => {
                const newValue = !showPrivacyAcceptedFilter;
                updatePrivacyFilter(1, newValue);
                setShowPrivacyAcceptedFilter(newValue);
              }}
            >
              <BooleanStatus value={showPrivacyAcceptedFilter} />
            </div>
          </div>
          <div className="flex items-center mr-4">
            <div className="mr-2">
              {t("Gallery.picturePrivacyFilter.rejected")}:
            </div>
            <div
              className="label flex items-center cursor-pointer"
              onClick={() => {
                const newValue = !showPrivacyRejectedFilter;
                updatePrivacyFilter(2, newValue);
                setShowPrivacyRejectedFilter(newValue);
              }}
            >
              <BooleanStatus value={showPrivacyRejectedFilter} />
            </div>
          </div>
          <div className="flex items-center">
            <div className="mr-2">
              {t("Gallery.picturePrivacyFilter.downloaded")}:
            </div>
            <div
              className="label flex items-center cursor-pointer"
              onClick={() => {
                const newValue = !showPrivacyDownloadedFilter;
                updatePrivacyFilter(3, newValue);
                setShowPrivacyDownloadedFilter(newValue);
              }}
            >
              <BooleanStatus value={showPrivacyDownloadedFilter} />
            </div>
          </div>
        </div>
      ) : null}
      {showPrivacyFilter || showBackgroundFilter ? (
        <div className="flex items-center text-sm rounded-md cursor-pointer">
          <div className="mr-4">{t("Gallery.picturebackgroundFilter")}:</div>
          <div
            className="label flex items-center cursor-pointer"
            onClick={() => {
              setActiveBackgroundFilterItems([]);
              setShowBackgroundFilter(!showBackgroundFilter);
              if (!showBackgroundFilter) {
                setFilterBy({ photo: true, video: false });
              }
            }}
          >
            <BooleanStatus value={showBackgroundFilter} />
          </div>
        </div>
      ) : null}
      {showBackgroundFilter ? (
        <div className="flex flex-row space-x-3 my-3 overflow-auto h-[85px]">
          {backgroundTypes.length > 0 ? (
            <SelectableItemWrapper
              key="all"
              active={allFilter.selectAll ? ["all"] : []}
              activeBackgroundFilterItems={activeBackgroundFilterItems}
              activateFilter={activateAllFilter}
            >
              <div
                // onClick={activateAllFilter}
                className="bg-gray-700 select-none text-white flex text-center items-center justify-center w-[150px] h-[100%]"
              >
                <p>{t("Gallery.selectAll")}</p>
              </div>
            </SelectableItemWrapper>
          ) : null}
          {backgroundTypes.map((background) => {
            if (background.mediaType === MediaEnum.Photo) {
              const groupKey = getBackgroundGroupKey(background.backgroundFileName);
              const active = activeBackgroundFilterItems.filter(
                (activeBackgroundFilterItem) =>
                  activeBackgroundFilterItem === groupKey
              );

              const activateFilter = () => {
                if (isEmpty(active)) {
                  if (groupKey) {
                    newActiveBackgroundFilterItems.push(groupKey);
                  }
                } else {
                  const currentIndex = newActiveBackgroundFilterItems.findIndex(
                    (nabfi) => nabfi === groupKey
                  );
                  newActiveBackgroundFilterItems.splice(currentIndex, 1);
                }
                setActiveBackgroundFilterItems(newActiveBackgroundFilterItems);
              };

              if (!background.backgroundFileName) {
                return (
                  <SelectableItemWrapper
                    key={background.id}
                    active={active}
                    activeBackgroundFilterItems={activeBackgroundFilterItems}
                    activateFilter={() => {
                      setAllFilter({
                        selectAll: allFilter.selectAll,
                        original: !allFilter.original,
                      });
                      activateFilter();
                    }}
                  >
                    <div
                      // onClick={activateFilter}
                      className="bg-gray-700 select-none text-white flex text-center items-center justify-center w-[150px] h-[100%]"
                    >
                      <p>{t("Gallery.originalPicture")}</p>
                    </div>
                  </SelectableItemWrapper>
                );
              } else {
                return (
                  <SelectableItemWrapper
                    key={background.backgroundFileName}
                    active={active}
                    activeBackgroundFilterItems={activeBackgroundFilterItems}
                    activateFilter={activateFilter}
                  >
                    <div className="relative w-[150px] h-full">
                      <Image
                        src={background.thumbnail}
                        width={150}
                        height={100}
                        alt={background.id}
                        id={background.id}
                        // onClick={activateFilter}
                      />
                      {/* Group name label in bottom-left corner with truncation and tooltip */}
                      {/* max-w-[140px] is intentionally 10px less than image width (150px) to prevent overflow */}
                      <span
                        className="absolute bottom-0 left-0 bg-black/60 text-white text-xs px-1 py-0.5 rounded-tr truncate max-w-[140px]"
                        title={groupKey || ""}
                      >
                        {groupKey}
                      </span>
                    </div>
                  </SelectableItemWrapper>
                );
              }
            } else {
              return null;
            }
          })}
        </div>
      ) : null}
    </div>
  ) : null;
}
